import React, { forwardRef, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';

/**
 * Positions a portaled panel relative to a trigger element and renders it
 * into `document.body`. Shared positioning primitive behind every Pegment
 * floating dropdown (`PgNavBottom`'s business menu/help submenu,
 * `PgTopbar`'s apps switcher):
 *
 *   - `'above'` — prefers opening above the anchor, flips below if there's
 *     not enough room, clamps horizontally to the viewport.
 *   - `'below'` — prefers opening below the anchor, right-aligned to it,
 *     flips above if there's not enough room, clamps horizontally.
 *   - `'below-start'` — same as `'below'`, but left-aligned to the anchor.
 *   - `'right'` — opens beside the anchor, clamped to the viewport.
 *
 * Not documented as a primary component in pegment.md — it has no visual
 * output of its own beyond positioning `.pg-dropdown-floating` and its
 * placement-specific modifier class; see pegment.css's "FLOATING DROPDOWN"
 * section for the actual styling.
 */
const PgFloatingPanel = forwardRef(({ anchorRef, placement, width, className, children }, ref) => {
    const localRef = useRef(null);
    useImperativeHandle(ref, () => localRef.current);
    const [style, setStyle] = useState({ position: 'fixed', top: 0, left: 0, width, opacity: 0 });

    useLayoutEffect(() => {
        let frame;
        const position = () => {
            const anchor = anchorRef.current;
            const panel = localRef.current;
            // A panel that mounts in the same commit as its anchor (e.g. one that
            // renders already open) runs this effect before the anchor's ref is
            // attached — try again on the next frame instead of staying hidden.
            if (!anchor || !panel) {
                frame = window.requestAnimationFrame(position);
                return;
            }

            const anchorRect = anchor.getBoundingClientRect();
            const panelRect = panel.getBoundingClientRect();
            const vw = window.innerWidth;
            const vh = window.innerHeight;

            let left;
            let top;

            if (placement === 'right') {
                left = anchorRect.right + 10;
                if (left + panelRect.width > vw - 8) left = Math.max(8, vw - panelRect.width - 8);
                top = anchorRect.top;
                if (top + panelRect.height > vh - 8) top = Math.max(8, vh - panelRect.height - 8);
            } else if (placement === 'below' || placement === 'below-start') {
                if (placement === 'below-start') {
                    left = anchorRect.left;
                } else {
                    left = anchorRect.right - panelRect.width;
                    if (left < 8) left = Math.max(8, anchorRect.left);
                }
                if (left + panelRect.width > vw - 8) left = Math.max(8, vw - panelRect.width - 8);
                top = anchorRect.bottom + 8;
                if (top + panelRect.height > vh - 8) {
                    top = anchorRect.top - panelRect.height - 8;
                    if (top < 8) top = Math.max(8, vh - panelRect.height - 8);
                }
            } else {
                left = anchorRect.left;
                if (left + panelRect.width > vw - 8) left = Math.max(8, vw - panelRect.width - 8);
                top = anchorRect.top - panelRect.height - 8;
                if (top < 8) {
                    top = anchorRect.bottom + 8;
                    if (top + panelRect.height > vh - 8) top = Math.max(8, vh - panelRect.height - 8);
                }
            }

            setStyle({ position: 'fixed', top, left, width, opacity: 1 });
        };
        position();
        return () => window.cancelAnimationFrame(frame);
    }, [anchorRef, placement, width]);

    if (typeof document === 'undefined') return null;

    const placementClass = placement === 'right'
        ? 'pg-dropdown-sub'
        : placement === 'below' || placement === 'below-start'
            ? 'pg-dropdown-below'
            : 'pg-dropdown-above';

    const panelClass = ['pg-dropdown-floating', placementClass, className].filter(Boolean).join(' ');

    return createPortal(
        <div ref={localRef} className={panelClass} style={style}>
            {children}
        </div>,
        document.body,
    );
});
PgFloatingPanel.displayName = 'PgFloatingPanel';

PgFloatingPanel.propTypes = {
    /** Ref to the trigger element the panel positions itself against. */
    anchorRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({ current: PropTypes.any }),
    ]).isRequired,
    placement: PropTypes.oneOf(['above', 'below', 'below-start', 'right']),
    width: PropTypes.number,
    className: PropTypes.string,
    children: PropTypes.node,
};

PgFloatingPanel.defaultProps = {
    placement: 'above',
    width: undefined,
    className: '',
    children: null,
};

export default PgFloatingPanel;
